// App.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
// --- UPDATED: Import getDocs, collection, and query ---
import { doc, getDoc, setDoc, getDocs, collection, query, where, onSnapshot } from 'firebase/firestore'; 
import { auth, db } from './firebase/config';
// --- UPDATED: Import the new notification template ---
import { sendNotificationEmail, newUserRegistrationTemplate } from './firebase/notifications';
import Navbar from './components/common/Navbar';
import LoadingSpinner from './components/common/LoadingSpinner';
import GenericModal from './components/common/GenericModal';
import HomePage from './modules/home/HomePage';
import AdminModule from './modules/admin/AdminModule';
import FinanceModule from './modules/finance/FinanceModule';
import ProcurementModule from './modules/procurement/ProcurementModule';
import PublicVendorForm from './modules/vendor/PublicVendorForm';
import HRPortal from './modules/hr/HRPortal';
import LeaveModule from './modules/leaves/LeaveModule';
import ExpenseModule from './modules/expenses/ExpenseModule';
import UserProfileModal from './modules/hr/UserProfileModal';
import LoginPage from './components/auth/LoginPage';
import TimeCoachModule from './modules/timecoach/TimeCoachModule';
import 'react-phone-number-input/style.css';
import PerformanceModule from './modules/performance/PerformanceModule';
import PeerAppreciation from './modules/peer/PeerAppreciation';
import ProfessionalDevelopment from './modules/pd/ProfessionalDevelopment';
import FeedbackModule from './modules/feedback/FeedbackModule';
import SupportWidget from './components/common/SupportWidget';
import OfficeScheduler from './modules/office/OfficeScheduler'; 
import ITCenter from './modules/it/ITCenter';


const BRAND_LOGO_URL = '/Brand_logo.png';
const FAVICON_URL = '/Brandmark.png';

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/vendor-form" element={<PublicVendorForm />} />
                <Route path="/*" element={<AdminPortal />} />
            </Routes>
        </Router>
    );
}

function AdminPortal() {
    const [view, setView] = useState('home');
    const [user, setUser] = useState(undefined);
    const [userProfile, setUserProfile] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [initialHrTab, setInitialHrTab] = useState('directory');    
    const [modalContent, setModalContent] = useState({ title: '', message: '' });
    
    // --- NEW: State for the self-edit profile modal ---
    const [isMyProfileModalOpen, setIsMyProfileModalOpen] = useState(false);
    // --- NEW: State to hold all users for the manager dropdown ---
    const [allUsers, setAllUsers] = useState([]);    

    const appId = process.env.REACT_APP_PROJECT_ID || "admin-c78c6";

    useEffect(() => {
        if (!user) return;

        const checkMyClaims = async () => {
            try {
                // Force a token refresh to get the latest claims
                const idTokenResult = await user.getIdTokenResult(true); 
                console.log("MY CUSTOM CLAIMS:", idTokenResult.claims);
            } catch (error) {
                console.error("Error checking claims:", error);
            }
        };

        checkMyClaims();
    }, [user]); // This will run every time the user object loads

    useEffect(() => {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = FAVICON_URL;
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                const userDocRef = doc(db, `/artifacts/${appId}/users/${currentUser.uid}`);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    setUserProfile({ id: userDocSnap.id, ...userDocSnap.data(), uid: currentUser.uid });
                } else {
                    console.log("User profile not found, creating a new one...");
                    try {
                        const newUserProfile = {
			    id: currentUser.uid,
                            uid: currentUser.uid,
                            name: currentUser.displayName,
                            email: currentUser.email,
			                photoURL: currentUser.photoURL,
                            role: 'User',
                            department: 'Unassigned',
                            status: 'Pending',
                            createdAt: new Date(),
                            updatedAt: new Date(),
                        };
                        await setDoc(userDocRef, newUserProfile);
                        setUserProfile(newUserProfile);

                        // --- NEW: Notification logic for new user registration ---
                        try {
                            const adminQuery = query(collection(db, `/artifacts/${appId}/users`), where("role", "==", "Admin"));
                            const adminSnapshot = await getDocs(adminQuery);
                            const adminEmails = adminSnapshot.docs.map(doc => doc.data().email);

                            if (adminEmails.length > 0) {
                                const emailContent = newUserRegistrationTemplate(newUserProfile);
                                await sendNotificationEmail({
                                    to: adminEmails,
                                    ...emailContent,
                                    appId
                                });
                            }
                        } catch (notificationError) {
                            console.error("Failed to send new user notification to admins:", notificationError);
                        }
                        // --- END of new notification logic ---

                    } catch (error) {
                        console.error("Error creating user profile:", error);
                    }
                }
            } else {
                setUser(null);
                setUserProfile(null);
            }
        });
        return () => unsubscribe();
    }, [appId]);
    
    // --- NEW: useEffect to fetch all users for the profile modal's manager dropdown ---
    useEffect(() => {
        const usersCollectionRef = collection(db, `/artifacts/${appId}/users`);
        const q = query(usersCollectionRef, where("status", "==", "Approved"));
        
        const unsub = onSnapshot(q, (snapshot) => {
            const usersData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllUsers(usersData);
        });

        return () => unsub();
    }, [appId]);

	// ... (rest of the App.js file is unchanged) ...

    const handleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Error during sign-in:", error);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setView('home');
        } catch (error) {
            console.error("Error during sign-out:", error);
        }
    };

    const navigateTo = (mainView, subView = null) => {
        if (mainView === 'hr' && subView) {
            setInitialHrTab(subView);
        }
        setView(mainView);
    };
        
    const showModal = (title, message) => {
        setModalContent({ title, message });
        setIsModalOpen(true);
    };

    if (user === undefined || (user && !userProfile)) {
        return <LoadingSpinner />;
    }

    if (!user) {
        return <LoginPage onSignIn={handleSignIn} />;
    }

    if (userProfile.status === 'Pending') {
        return <PendingApprovalScreen />;
    }

    if (userProfile.status === 'Deactivated') {
        return <DeactivatedScreen onSignOut={handleSignOut} />;
    }

    let currentModuleComponent;
    switch(view) {
        case 'procurement':
            currentModuleComponent = <ProcurementModule userProfile={userProfile} showModal={showModal} appId={appId} />;
            break;
	case 'it':
            currentModuleComponent = <ITCenter userProfile={userProfile} appId={appId} />;
            break;        
	case 'finance':
            currentModuleComponent = <FinanceModule userProfile={userProfile} appId={appId} />;
            break;
        case 'admin':
            currentModuleComponent = <AdminModule appId={appId} />;
            break;
        case 'hr':
            currentModuleComponent = <HRPortal userProfile={userProfile} appId={appId} initialTab={initialHrTab} />;
            break;
	case 'leaves':
            currentModuleComponent = <LeaveModule userProfile={userProfile} showModal={showModal} appId={appId} />;
            break;
	case 'expenses':
            currentModuleComponent = <ExpenseModule userProfile={userProfile} showModal={showModal} appId={appId} />;
            break;
	case 'timeCoach':
            currentModuleComponent = <TimeCoachModule userProfile={userProfile} appId={appId} />;
            break;
	case 'performance':
            currentModuleComponent = <PerformanceModule userProfile={userProfile} appId={appId} showModal={showModal} />;
            break;
        case 'pd':
            currentModuleComponent = <ProfessionalDevelopment userProfile={userProfile} appId={appId} />;
            break;
	case 'peer':
            currentModuleComponent = <PeerAppreciation userProfile={userProfile} appId={appId} />;
            break;
	case 'feedback':
            currentModuleComponent = <FeedbackModule userProfile={userProfile} appId={appId} />;
            break;
	case 'office':
            currentModuleComponent = <OfficeScheduler userProfile={userProfile} appId={appId} />;
            break;
	case 'home':        
	default:
            currentModuleComponent = <HomePage setView={setView} navigateTo={navigateTo} appId={appId} userProfile={userProfile} />;
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-[#213368]">
            {/* --- NEW: Pass the onOpenMyProfile handler to the Navbar --- */}
            <Navbar 
                appId={appId}
		user={user} 
                setView={setView} 
                currentView={view} 
                userProfile={userProfile} 
                onSignOut={handleSignOut}
                onOpenMyProfile={() => setIsMyProfileModalOpen(true)}
            />
            <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto print:p-0 print:max-w-none">
                {currentModuleComponent}
            </main>
            <footer className="text-center text-xs text-gray-400 p-4 print:hidden">
                <p>User ID: {user.uid}</p>
                <p>App ID: {appId}</p>
            </footer>
            {isModalOpen && <GenericModal title={modalContent.title} message={modalContent.message} onClose={() => setIsModalOpen(false)} />}
            
            {/* --- NEW: Conditionally render the UserProfileModal for self-editing --- */}
            {isMyProfileModalOpen && userProfile && (
                <UserProfileModal
                    appId={appId}
                    existingUser={userProfile}
                    onClose={() => setIsMyProfileModalOpen(false)}
                />
            )}
	    <div className="print:hidden">
                {userProfile && <SupportWidget userProfile={userProfile} />}
            </div>
        </div>
    );
}

/*
function LoginScreen({ onSignIn }) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
            <img src={BRAND_LOGO_URL} alt="LINC Education Logo" className="h-16 w-auto mb-6 object-contain"/>
            <h1 className="text-3xl font-bold text-[#213368] mb-8 text-center px-4">LINC Education Admin Platform</h1>
            <button
                onClick={onSignIn}
                className="flex items-center justify-center px-6 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-gray-700 hover:bg-gray-100 transition"
            >
                <svg className="w-6 h-6 mr-3" viewBox="0 0 48 48">
                    <path fill="#4285F4" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path>
                    <path fill="#34A853" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path>
                    <path fill="#FBBC05" d="M27.461 44.5l-6.571-4.82c-1.506 1.018-3.21 1.62-5.023 1.821C10.442 41.645 6 35.84 6 28.539c0-2.525.683-4.914 1.866-6.961l-6.571-4.82C.54 20.138 0 24.061 0 28.539c0 7.492 4.896 13.868 11.601 16.345l.001-.001z"></path>
                    <path fill="#EA4335" d="M24 48c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 40.61 26.715 42 24 42c-5.222 0-9.519-3.512-11.01-8.224l-6.571 4.82C6.343 43.963 14.594 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                Sign in with Google
            </button>
        </div>
    );
}
*/

function PendingApprovalScreen() {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-4">
            <img src={BRAND_LOGO_URL} alt="LINC Education Logo" className="h-12 w-auto mb-4 object-contain"/>
            <h1 className="text-3xl font-bold text-[#213368] mb-4">Registration Received</h1>
            <p className="text-lg text-gray-600 max-w-md">
                Thank you for registering. Your account is currently pending approval from an administrator.
            </p>
        </div>
    );
}

function DeactivatedScreen({ onSignOut }) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-4">
            <img src={BRAND_LOGO_URL} alt="LINC Education Logo" className="h-12 w-auto mb-4 object-contain"/>
            <h1 className="text-3xl font-bold text-red-600 mb-4">Account Deactivated</h1>
            <p className="text-lg text-gray-600 max-w-md mb-6">
                Your account has been deactivated. Please contact an administrator for assistance.
            </p>
            <button
                onClick={onSignOut}
                className="px-6 py-2 bg-gray-600 text-white font-medium rounded-lg shadow-md hover:bg-gray-700"
            >
                Sign Out
            </button>
        </div>
    );
}

export default App;